import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';

const CELL_SIZE = 15;
const GRID_COLOR = '#e5e7eb';
const SELECTED_COLOR = '#3b82f6';

export default function AssignmentCanvas({ exhibition, request, onAssigned }) {
  const canvasRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    loadLayout();
  }, [exhibition]);

  useEffect(() => {
    if (layout) {
      drawCanvas();
    }
  }, [layout, selectedCells]);

  const loadLayout = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/layouts/${exhibition.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLayout(res.data);
    } catch (error) {
      toast.error('Kroki yüklenemedi');
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !layout) return;

    const ctx = canvas.getContext('2d');
    canvas.width = layout.grid_width * CELL_SIZE;
    canvas.height = layout.grid_height * CELL_SIZE;

    // Draw grid and cells
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;

    for (let y = 0; y < layout.grid_height; y++) {
      for (let x = 0; x < layout.grid_width; x++) {
        const cell = layout.grid_data[y]?.[x];
        
        // Fill cell based on type
        if (cell?.type === 'stand') {
          if (cell.assignedTo) {
            ctx.fillStyle = '#fecaca'; // Already assigned
          } else {
            ctx.fillStyle = '#d1fae5'; // Available
          }
          ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }

        // Highlight selected cells
        const isSelected = selectedCells.some(c => c.x === x && c.y === y);
        if (isSelected) {
          ctx.fillStyle = SELECTED_COLOR;
          ctx.globalAlpha = 0.5;
          ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
          ctx.globalAlpha = 1;
        }

        // Draw grid
        ctx.strokeRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      }
    }
  };

  const handleCanvasClick = (e) => {
    if (!layout) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const y = Math.floor((e.clientY - rect.top) / CELL_SIZE);

    if (x >= 0 && x < layout.grid_width && y >= 0 && y < layout.grid_height) {
      const cell = layout.grid_data[y]?.[x];
      
      // Only allow selection of available stand cells
      if (cell?.type === 'stand' && !cell.assignedTo) {
        const existingIndex = selectedCells.findIndex(c => c.x === x && c.y === y);
        
        if (existingIndex >= 0) {
          // Deselect
          setSelectedCells(selectedCells.filter((_, i) => i !== existingIndex));
        } else {
          // Select
          setSelectedCells([...selectedCells, { x, y }]);
        }
      }
    }
  };

  const handleAssign = async () => {
    if (selectedCells.length === 0) {
      toast.error('Lütfen en az bir hücre seçin');
      return;
    }

    if (selectedCells.length < request.area_required) {
      toast.error(`En az ${request.area_required} m² alan seçmelisiniz`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/assignments`,
        {
          request_id: request.id,
          assigned_cells: selectedCells
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update layout with assignment
      const updatedGridData = JSON.parse(JSON.stringify(layout.grid_data));
      selectedCells.forEach(({ x, y }) => {
        if (updatedGridData[y] && updatedGridData[y][x]) {
          updatedGridData[y][x].assignedTo = request.company_name;
        }
      });

      await axios.put(
        `${API}/layouts/${exhibition.id}`,
        {
          exhibition_id: exhibition.id,
          grid_width: layout.grid_width,
          grid_height: layout.grid_height,
          grid_data: updatedGridData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Stant başarıyla atandı!');
      onAssigned();
    } catch (error) {
      toast.error('Stant atanamadı');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-200 border" />
            <span>Müsait</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-200 border" />
            <span>Dolu</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 opacity-50 border" />
            <span>Seçili</span>
          </div>
        </div>
        <p className="text-sm">
          Seçilen alan: <strong>{selectedCells.length} m²</strong> / {request.area_required} m²
        </p>
      </div>

      <div className="overflow-auto border rounded-lg" style={{ maxHeight: '500px' }}>
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          className="cursor-pointer"
          data-testid="assignment-canvas"
        />
      </div>

      <Button
        onClick={handleAssign}
        disabled={selectedCells.length < request.area_required}
        className="w-full"
        data-testid="confirm-assignment-btn"
      >
        <CheckCircle className="w-4 h-4 mr-2" />
        Atamayı Onayla ({selectedCells.length} m²)
      </Button>
    </div>
  );
}